(() => {
  const ROOT_SELECTOR = '[data-otheriver-service-motion]';
  const STATE_KEY = '__otheriverServiceMotion';
  const CONTROLLER_KEY = '__otheriverServicesMotionController';
  const MAX_LIBRARY_ATTEMPTS = 120;

  if (window[CONTROLLER_KEY]) {
    window[CONTROLLER_KEY].refresh();
    return;
  }

  function findRoot(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) return scope;
    return scope.querySelector?.(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
  }

  function destroy(root) {
    const state = root?.[STATE_KEY];
    if (!state) return;

    state.cleanups.forEach((cleanup) => cleanup());
    state.context?.revert();
    root.classList.remove('is-motion-enhanced');
    delete root[STATE_KEY];
  }

  function init(scope = document, attempt = 0) {
    const root = findRoot(scope);
    if (!root || root[STATE_KEY]) return;
    if (root.dataset.motionEnabled !== 'true') return;

    if (!window.gsap || !window.ScrollTrigger) {
      if (attempt < MAX_LIBRARY_ATTEMPTS) {
        window.setTimeout(() => init(scope, attempt + 1), 50);
      }
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    const page = root.closest('main') || document.body;
    const intensity = root.dataset.motionIntensity === 'strong' ? 'strong' : 'balanced';
    const isDesktop = window.matchMedia('(min-width: 64rem)').matches;
    const travel = intensity === 'strong' ? (isDesktop ? 76 : 34) : (isDesktop ? 42 : 24);
    const cleanups = [];

    root.classList.add('is-motion-enhanced');

    const context = gsap.context(() => {
      const hero = page.querySelector('.otheriver-hero');
      const heroEyebrow = hero?.querySelector('.otheriver-eyebrow');
      const heroHeading = hero?.querySelector('h1');
      const heroCopy = hero?.querySelector('.otheriver-copy');
      const heroActions = hero?.querySelector('.otheriver-actions');

      if (hero) {
        const heroTimeline = gsap.timeline({
          defaults: { duration: intensity === 'strong' ? 1.05 : 0.8, ease: 'power4.out' }
        });

        if (heroEyebrow) {
          heroTimeline.fromTo(
            heroEyebrow,
            { autoAlpha: 0, x: -24, filter: 'blur(8px)' },
            { autoAlpha: 1, x: 0, filter: 'blur(0px)' }
          );
        }

        if (heroHeading) {
          heroTimeline.fromTo(
            heroHeading,
            {
              autoAlpha: 0,
              y: travel,
              scale: intensity === 'strong' ? 0.96 : 0.985,
              clipPath: 'inset(0 0 100% 0)',
              filter: 'blur(10px)'
            },
            {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              clipPath: 'inset(0 0 0% 0)',
              filter: 'blur(0px)'
            },
            '-=0.55'
          );
        }

        if (heroCopy) {
          heroTimeline.fromTo(
            heroCopy,
            { autoAlpha: 0, y: 30 },
            { autoAlpha: 1, y: 0, duration: 0.75 },
            '-=0.5'
          );
        }

        if (heroActions) {
          heroTimeline.fromTo(
            heroActions.children,
            { autoAlpha: 0, y: 22, scale: 0.96 },
            { autoAlpha: 1, y: 0, scale: 1, duration: 0.65, stagger: 0.1 },
            '-=0.42'
          );
        }

        gsap.to(hero, {
          backgroundPosition: intensity === 'strong' ? '58% 42%' : '54% 46%',
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.8
          }
        });
      }

      const scan = root.querySelector('.otheriver-service-catalog__scan');
      const orb = root.querySelector('.otheriver-service-catalog__orb');
      const nodes = root.querySelectorAll('.otheriver-service-catalog__node');

      if (scan) {
        gsap.fromTo(
          scan,
          { yPercent: -120, autoAlpha: 0.2 },
          {
            yPercent: 720,
            autoAlpha: 0.85,
            duration: intensity === 'strong' ? 5 : 7,
            ease: 'none',
            repeat: -1
          }
        );
      }

      if (orb) {
        gsap.to(orb, {
          yPercent: 55,
          rotate: intensity === 'strong' ? 35 : 18,
          scale: intensity === 'strong' ? 1.12 : 1.05,
          ease: 'none',
          scrollTrigger: {
            trigger: root,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        });
      }

      nodes.forEach((node, index) => {
        gsap.to(node, {
          x: index % 2 === 0 ? 34 : -28,
          y: index === 1 ? 42 : -30,
          scale: intensity === 'strong' ? 1.35 : 1.15,
          duration: 2.8 + index * 0.65,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true
        });
      });

      const catalogHeading = root.querySelector('[data-otheriver-catalog-heading]');
      if (catalogHeading) {
        const headingParts = [
          catalogHeading.querySelector('.otheriver-eyebrow'),
          catalogHeading.querySelector('.otheriver-heading'),
          catalogHeading.querySelector('.otheriver-copy')
        ].filter(Boolean);

        gsap.fromTo(
          headingParts,
          { autoAlpha: 0, y: travel * 0.7, filter: 'blur(8px)' },
          {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.9,
            stagger: 0.12,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: catalogHeading,
              start: 'top 82%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

      const rows = Array.from(root.querySelectorAll('[data-otheriver-service-row]'));
      rows.forEach((row, index) => {
        const rowIndex = row.querySelector('.otheriver-service-row__index');
        const intro = row.querySelector('.otheriver-service-row__intro');
        const details = row.querySelector('.otheriver-service-row__details');
        const tags = row.querySelectorAll('.otheriver-service-row__tags li');
        const direction = index % 2 === 0 ? -1 : 1;

        const rowTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: row,
            start: 'top 82%',
            end: 'top 48%',
            scrub: intensity === 'strong' ? 0.7 : 0.45
          }
        });

        if (rowIndex) {
          rowTimeline.fromTo(
            rowIndex,
            {
              autoAlpha: 0,
              x: isDesktop ? direction * travel : 0,
              y: isDesktop ? 0 : travel * 0.65,
              rotateZ: isDesktop ? direction * 2 : 0
            },
            { autoAlpha: 1, x: 0, y: 0, rotateZ: 0, ease: 'power3.out' },
            0
          );
        }

        if (intro) {
          rowTimeline.fromTo(
            intro,
            {
              autoAlpha: 0,
              x: isDesktop ? direction * travel * 0.75 : 0,
              y: isDesktop ? 0 : travel,
              filter: 'blur(8px)'
            },
            { autoAlpha: 1, x: 0, y: 0, filter: 'blur(0px)', ease: 'power3.out' },
            0.08
          );
        }

        if (details) {
          rowTimeline.fromTo(
            details,
            {
              autoAlpha: 0,
              x: isDesktop ? direction * -travel * 0.65 : 0,
              y: isDesktop ? 0 : travel * 0.8
            },
            { autoAlpha: 1, x: 0, y: 0, ease: 'power3.out' },
            0.15
          );
        }

        if (tags.length) {
          rowTimeline.fromTo(
            tags,
            { autoAlpha: 0, y: 18, scale: 0.9 },
            { autoAlpha: 1, y: 0, scale: 1, stagger: 0.07, ease: 'back.out(1.7)' },
            0.48
          );
        }

        gsap.fromTo(
          row,
          { '--otheriver-row-progress': '0%' },
          {
            '--otheriver-row-progress': '100%',
            ease: 'none',
            scrollTrigger: {
              trigger: row,
              start: 'top 82%',
              end: 'bottom 38%',
              scrub: 0.6
            }
          }
        );

        ScrollTrigger.create({
          trigger: row,
          start: 'top 56%',
          end: 'bottom 44%',
          toggleClass: { targets: row, className: 'is-active' }
        });

        const handlePointerMove = (event) => {
          const bounds = row.getBoundingClientRect();
          row.style.setProperty('--otheriver-pointer-x', `${event.clientX - bounds.left}px`);
          row.style.setProperty('--otheriver-pointer-y', `${event.clientY - bounds.top}px`);
        };

        row.addEventListener('pointermove', handlePointerMove, { passive: true });
        cleanups.push(() => row.removeEventListener('pointermove', handlePointerMove));
      });

      const process = page.querySelector('[data-otheriver-process]');
      if (process) {
        const processHeading = process.querySelector('[data-otheriver-process-heading]');
        const processList = process.querySelector('.otheriver-process');
        const progressLabel = process.querySelector('[data-otheriver-process-progress]');
        const steps = Array.from(process.querySelectorAll('[data-otheriver-process-step]'));

        if (processHeading) {
          gsap.fromTo(
            processHeading.children,
            { autoAlpha: 0, y: 48, filter: 'blur(8px)' },
            {
              autoAlpha: 1,
              y: 0,
              filter: 'blur(0px)',
              duration: 0.9,
              stagger: 0.14,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: processHeading,
                start: 'top 78%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        }

        steps.forEach((step, index) => {
          gsap.fromTo(
            step,
            {
              autoAlpha: 0,
              x: isDesktop ? (index % 2 === 0 ? 50 : -50) : 0,
              y: isDesktop ? 0 : 30
            },
            {
              autoAlpha: 1,
              x: 0,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: step,
                start: 'top 84%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        });

        if (processList && steps.length) {
          ScrollTrigger.create({
            trigger: processList,
            start: 'top 68%',
            end: 'bottom 42%',
            scrub: true,
            onUpdate: (self) => {
              const percent = Math.round(self.progress * 100);
              const activeIndex = Math.min(steps.length - 1, Math.floor(self.progress * steps.length));
              processList.style.setProperty('--otheriver-process-progress', `${percent}%`);
              if (progressLabel) progressLabel.textContent = `${String(percent).padStart(2, '0')}%`;
              steps.forEach((step, index) => step.classList.toggle('is-active', index === activeIndex));
            }
          });
        }
      }

      const manifesto = page.querySelector('.otheriver-manifesto');
      if (manifesto) {
        const manifestoTargets = [
          manifesto.querySelector('.otheriver-eyebrow'),
          manifesto.querySelector('.otheriver-manifesto__heading'),
          manifesto.querySelector('.otheriver-copy'),
          manifesto.querySelector('.otheriver-actions')
        ].filter(Boolean);

        gsap.fromTo(
          manifestoTargets,
          {
            autoAlpha: 0,
            y: intensity === 'strong' ? 64 : 36,
            scale: intensity === 'strong' ? 0.97 : 0.99,
            filter: 'blur(10px)'
          },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 1,
            stagger: 0.13,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: manifesto,
              start: 'top 78%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }
    }, page);

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh, { once: true });
    cleanups.push(() => window.removeEventListener('load', refresh));

    root[STATE_KEY] = { context, cleanups };
    window.requestAnimationFrame(refresh);
  }

  function refreshPageMotion() {
    const root = findRoot(document);
    if (root) destroy(root);
    window.requestAnimationFrame(() => init(document));
  }

  window[CONTROLLER_KEY] = { refresh: refreshPageMotion };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', refreshPageMotion);
  document.addEventListener('shopify:section:unload', refreshPageMotion);
})();
